import { CheckCircleIcon, ChevronRightIcon, MailIcon } from '@heroicons/react/solid'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'

export default function PaymentEventList() {
    const [ eventList, setEventList ] = useState([])
    const [ showAll, setShowAll ] = useState(false)
    const axiosPrivat = useAxiosPrivate();

    const now = useMemo(() => moment(), [])
    const currentYear = now.year()

    const filteredEvents = useMemo(() => {
        if (!eventList) return []
        const kept = eventList.filter((ev) => {
            if (showAll) return true
            if (!ev.event_start_date) return false
            return moment(ev.event_start_date).year() === currentYear
        })
        // sort by start date desc (as before)
        return kept.sort((a, b) => (a.event_start_date < b.event_start_date ? 1 : -1))
    }, [eventList, showAll, currentYear])

    const notFinished = useMemo(() => {
        return filteredEvents.filter((ev) => ev.event_end_date && moment(ev.event_end_date).isAfter(now))
    }, [filteredEvents, now])

    const finished = useMemo(() => {
        return filteredEvents.filter((ev) => !ev.event_end_date || moment(ev.event_end_date).isSameOrBefore(now))
    }, [filteredEvents, now])

    const initGetEventList = async () => {
        let isMounted = true;
        const controller = new AbortController();
    
        const getEventList = async () => {
            try {
                const response = await axiosPrivat.get('/event/all/org', {
                    signal: controller.signal
                });
                //console.log(response.data);
                isMounted && setEventList(response.data);
            } catch (err) {
                console.error(err);
            }
        }
    
        getEventList();
    
        return () => {
            isMounted = false;
            controller.abort();
        }
    }
    
    useEffect(() => {
        initGetEventList();
        return () => {
            setEventList([])
        }
    }, [])

  return (
    <>
    <h2 className="max-w-6xl mx-auto mt-8 px-4 text-lg leading-6 font-medium text-gray-900 sm:px-6 lg:px-8">
            Vælg et event
    </h2>

    <div className="px-4 sm:px-6 lg:px-8 mt-4 flex items-center justify-end">
        <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 hidden sm:inline">Vis</span>
            <button
                type="button"
                onClick={() => setShowAll(false)}
                className={(showAll ? 'text-gray-600 hover:text-gray-800' : 'bg-indigo-100 text-indigo-700') + ' rounded-md px-3 py-1.5 text-sm font-medium'}
            >
                I år ({currentYear})
            </button>
            <button
                type="button"
                onClick={() => setShowAll(true)}
                className={(showAll ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:text-gray-800') + ' rounded-md px-3 py-1.5 text-sm font-medium'}
            >
                Alle events
            </button>
        </div>
    </div>

    <div className="bg-white shadow overflow-hidden sm:rounded-md mt-6">
      <ul role="list" className="divide-y divide-gray-200">
        {notFinished.length > 0 && (
          <li className="bg-gray-50 px-4 py-2 text-left text-sm font-semibold text-gray-900 sm:px-6">
            Ikke afsluttet
          </li>
        )}
        {notFinished.map((application) => (
          <li key={application.id}>
            <Link to={'/betalinger/'+application.slug} className="block hover:bg-gray-50">
              <div className="flex items-center px-4 py-4 sm:px-6">
                <div className="min-w-0 flex-1 flex items-center">
                  <div className="flex-shrink-0">
                    <img className="h-12 w-12 rounded-full" src={application.img + "/36x36"} alt={application.name}  />
                  </div>
                  <div className="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                    <div>
                      <p className="text-sm text-left font-medium text-indigo-600 truncate">{application.name}</p>
                      <p className="mt-2 flex items-center text-sm text-gray-500">
                        <MailIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <span className="truncate">{application.org_name}</span>
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <div>
  
                        <p className="mt-2 flex items-center text-sm text-gray-500">
                          <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-green-400" aria-hidden="true" />
                          {moment(application.event_start_date).format("dddd, DD MMM HH:mm YYYY")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
              </div>
            </Link>
          </li>
        ))}

        {finished.length > 0 && (
          <li className="bg-gray-50 px-4 py-2 text-left text-sm font-semibold text-gray-900 sm:px-6">
            Afsluttet
          </li>
        )}
        {finished.map((application) => (
          <li key={application.id}>
            <Link to={'/betalinger/'+application.slug} className="block hover:bg-gray-50">
              <div className="flex items-center px-4 py-4 sm:px-6">
                <div className="min-w-0 flex-1 flex items-center">
                  <div className="flex-shrink-0">
                    <img className="h-12 w-12 rounded-full" src={application.img + "/36x36"} alt={application.name}  />
                  </div>
                  <div className="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                    <div>
                      <p className="text-sm text-left font-medium text-indigo-600 truncate">{application.name}</p>
                      <p className="mt-2 flex items-center text-sm text-gray-500">
                        <MailIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <span className="truncate">{application.org_name}</span>
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <div>
  
                        <p className="mt-2 flex items-center text-sm text-gray-500">
                          <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-green-400" aria-hidden="true" />
                          {moment(application.event_start_date).format("dddd, DD MMM HH:mm YYYY")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
    </>
  )
}